/**
 * StuckJobsWidget — surfaces silent failures on Governor Dashboard.
 *
 * Lists onboarding_jobs and brain_jobs that are stuck in queued/processing
 * state for more than `minutes` (default 5).
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { governanceAPI } from '../../../lib/governanceApi';
import { useSuperAdminTheme } from '../../../contexts/SuperAdminThemeContext';
import { AlertTriangle, Clock, RefreshCw, ArrowRight } from 'lucide-react';

const POLL_MS = 30_000;

export default function StuckJobsWidget({ minutes = 5 }) {
  const { theme } = useSuperAdminTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fmtAge = (mins) => {
    const m = Math.max(0, Math.round(Number(mins) || 0));
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ${m % 60}m`;
    const d = Math.floor(h / 24);
    return `${d}d ${h % 24}h`;
  };

  const fetchJobs = useCallback(async () => {
    try {
      const res = await governanceAPI.getStuckJobs(minutes);
      setData(res.data);
    } catch (e) {
      console.warn('[StuckJobs] fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [minutes]);

  useEffect(() => {
    fetchJobs();
    const t = setInterval(fetchJobs, POLL_MS);
    return () => clearInterval(t);
  }, [fetchJobs]);

  const cardBg = isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200';
  const total = data?.total_stuck || 0;
  const severity = total === 0 ? 'ok' : total < 3 ? 'warning' : 'critical';
  const sevColor = {
    ok:       isDark ? 'text-emerald-400' : 'text-emerald-600',
    warning:  isDark ? 'text-amber-400'   : 'text-amber-600',
    critical: isDark ? 'text-rose-400'    : 'text-rose-600',
  }[severity];

  return (
    <div className={`rounded-xl border p-5 ${cardBg}`} data-testid="stuck-jobs-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className={`w-5 h-5 ${sevColor}`} />
          <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Stuck Jobs
          </h3>
          <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-600'}`}>
            &gt; {minutes} min
          </span>
        </div>
        <button
          onClick={fetchJobs}
          className={`p-1.5 rounded-md transition ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-800' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
          data-testid="stuck-jobs-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading && !data ? (
        <div className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Loading…</div>
      ) : total === 0 ? (
        <div className={`text-sm ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
          All jobs flowing normally. No stuck work.
        </div>
      ) : (
        <div className="space-y-3">
          {(data.onboarding_stuck || []).length > 0 && (
            <div>
              <div className={`text-xs uppercase tracking-wider mb-2 ${isDark ? 'text-slate-500' : 'text-slate-600'}`}>
                Onboarding · {data.onboarding_stuck.length}
              </div>
              <div className="space-y-2">
                {data.onboarding_stuck.slice(0, 5).map((j) => (
                  <button
                    key={j.id}
                    onClick={() => navigate(`/governance/tenants/${j.tenant_id}`)}
                    className={`w-full flex items-center justify-between text-left px-3 py-2 rounded-md border transition ${isDark ? 'border-slate-800 hover:border-amber-700 hover:bg-amber-950/20' : 'border-slate-200 hover:border-amber-400 hover:bg-amber-50'}`}
                    data-testid={`stuck-onboarding-${j.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Clock className={`w-4 h-4 ${sevColor} shrink-0`} />
                      <div className="min-w-0">
                        <div className={`text-sm font-medium truncate ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
                          {j.tenant_id?.slice(0, 12)}… · phase: {j.phase || '—'}
                        </div>
                        <div className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-600'}`}>
                          {fmtAge(j.age_minutes)} old · {j.progress}% · {j.message?.slice(0, 60) || '—'}
                        </div>
                      </div>
                    </div>
                    <ArrowRight className={`w-4 h-4 shrink-0 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                  </button>
                ))}
              </div>
            </div>
          )}
          {(data.brain_stuck || []).length > 0 && (
            <div>
              <div className={`text-xs uppercase tracking-wider mb-2 ${isDark ? 'text-slate-500' : 'text-slate-600'}`}>
                Brain Jobs · {data.brain_stuck.length}
              </div>
              <div className="space-y-2">
                {data.brain_stuck.slice(0, 5).map((j) => (
                  <div
                    key={j.id}
                    className={`flex items-center justify-between px-3 py-2 rounded-md border ${isDark ? 'border-slate-800' : 'border-slate-200'}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Clock className={`w-4 h-4 ${sevColor} shrink-0`} />
                      <div className="min-w-0">
                        <div className={`text-sm font-medium truncate ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
                          {j.job_type} · tenant {j.tenant_id?.slice(0, 12)}…
                        </div>
                        <div className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-600'}`}>
                          {fmtAge(j.age_minutes)} stuck · retry_count={j.retry_count || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
